import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import {
  createApi,
  fakeBaseQuery,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify";
import {
  adminAuthTokenCookieKey,
  API_BASE_URL,
  API_REQUEST_TIMEOUT,
  clientAuthTokenCookieKey,
  salesAuthTokenCookieKey,
} from "../utils/constants";
import { getCookie } from "../utils/helpers";

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    timeout: API_REQUEST_TIMEOUT,
    prepareHeaders: (headers) => {
      const token = getCookie(clientAuthTokenCookieKey);
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

  try {
    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error) {
      if (result.error.status === 401) {
        return result;
      } else if (result.error.status === 403) {
        toast.error("Forbidden - 403");
        return result;
      } else if (result.error.status === 422) {
        // toast.error("Unprocessable entity");
        return result;
      }
    }

    return result;
  } catch (err) {
    console.error("Unexpected error:", err);
    throw err;
  }
};

const adminCustomBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    timeout: API_REQUEST_TIMEOUT,
    prepareHeaders: (headers) => {
      const token = getCookie(adminAuthTokenCookieKey);
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

  try {
    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error) {
      if (result.error.status === 401) {
        return result;
      } else if (result.error.status === 403) {
        toast.error("Forbidden - 403");
        return result;
      } else if (result.error.status === 422) {
        // toast.error("Unprocessable entity");
        return result;
      }
    }

    return result;
  } catch (err) {
    console.error("Unexpected error:", err);
    throw err;
  }
};


const salesCustomBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    timeout: API_REQUEST_TIMEOUT,
    prepareHeaders: (headers) => {
      const token = getCookie(salesAuthTokenCookieKey);
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

  try {
    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error) {
      if (result.error.status === 401) {
        return result;
      } else if (result.error.status === 403) {
        toast.error("Forbidden - 403");
        return result;
      } else if (result.error.status === 422) {
        // toast.error("Unprocessable entity");
        return result;
      }
    }

    return result;
  } catch (err) {
    console.error("Unexpected error:", err);
    throw err;
  }
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: customBaseQuery,
  endpoints: () => ({}),
});

export const adminBaseApi = createApi({
  reducerPath: "adminApi",
  baseQuery: adminCustomBaseQuery,
  endpoints: () => ({}),
});

export const salesBaseApi = createApi({
  reducerPath: "salesApi",
  baseQuery: salesCustomBaseQuery,
  endpoints: () => ({}),
});

export type BlogApiError = { status: "CUSTOM_ERROR"; error: string };

// Sanity is queried directly via its client inside each endpoint's `queryFn`,
// so this api has no HTTP base query of its own.
export const blogApi = createApi({
  reducerPath: "blogApi",
  baseQuery: fakeBaseQuery<BlogApiError>(),
  endpoints: () => ({}),
});

// The IP geolocation lookup is called directly against a third-party service
// inside its endpoint's `queryFn`, so this api has no HTTP base query of its own.
export const geoApi = createApi({
  reducerPath: "geoApi",
  baseQuery: fakeBaseQuery(),
  endpoints: () => ({}),
});